import api from './api';
import { Message } from '../contexts/ConversationContext';
import { createModelId, parseModelId } from './modelService';

export interface ChatSession {
  id: string;
  persona_id: string;
  persona_name: string;
  message_count: number;
  last_message?: string;
  last_activity: string;
  created_at: string;
  updated_at: string;
  messages?: Message[];
  metadata?: Record<string, any>;
}

export async function createPersonaChat(
  personaId: string,
  initialMessage?: string,
  metadata?: Record<string, any>
): Promise<{ session_id: string; persona_id: string; created_at: string; initial_response?: any }> {
  // Ensure metadata includes session_type
  const enhancedMetadata = {
    ...(metadata || {}),
    session_type: "chat"  // Always set this explicitly
  };
  
  console.log("Creating persona chat with metadata:", enhancedMetadata);
  
  // Add model_id to metadata if provided
  if (enhancedMetadata.model_id) {
    // Ensure model_id is in standardized format
    const { provider, modelName } = parseModelId(enhancedMetadata.model_id);
    enhancedMetadata.model_id = createModelId(provider, modelName);
  }
  
  const response = await api.post(`/chat/persona/${personaId}`, {
    initial_message: initialMessage,
    metadata: enhancedMetadata
  });
  return response.data;
}

export async function sendMessage(
  sessionId: string,
  message: string,
  metadata?: Record<string, any>
): Promise<Message> {
  // Add model_id to metadata if provided
  if (metadata?.model_id) {
    // Ensure model_id is in standardized format
    const { provider, modelName } = parseModelId(metadata.model_id);
    metadata.model_id = createModelId(provider, modelName);
  }
  
  // Persona responses routinely take 30–60s (tool loops, large contexts, slow
  // models). The default 60s timeout in api.ts aborts mid-flight while the
  // backend is still generating — the caller sees a generic error and on
  // refresh the actual response shows up from history. 5-minute ceiling
  // matches the budget persona + tool-round execution can realistically need.
  const response = await api.post(`/chat/sessions/${sessionId}/messages`, {
    message,
    metadata
  }, { timeout: 300000 });
  return response.data;
}

export async function getChatSessions(): Promise<ChatSession[]> {
  const response = await api.get('/chat/sessions');
  return response.data;
}

export async function getChatSession(sessionId: string): Promise<ChatSession> {
  const response = await api.get(`/chat/sessions/${sessionId}`);
  return response.data;
}

export async function getChatMessages(sessionId: string): Promise<Message[]> {
  const response = await api.get(`/chat/sessions/${sessionId}/messages`);
  return response.data;
}

export async function deleteChatSession(sessionId: string): Promise<void> {
  await api.delete(`/chat/sessions/${sessionId}`);
}

export async function setChatSessionModel(
  sessionId: string,
  modelId: string
): Promise<{ session_id: string; model_id: string; provider: string; model_name: string }> {
  // Ensure model_id is in standardized format
  const { provider, modelName } = parseModelId(modelId);
  const standardizedModelId = createModelId(provider, modelName);
  
  const response = await api.post(`/chat/sessions/${sessionId}/model`, {
    model_id: standardizedModelId
  });
  return response.data;
}
